import type { RefObject } from "react";

// One shared mechanism to close open popovers (combobox dropdowns, tooltips) when a scroll container
// scrolls — Floating UI repositions but does not close, leaving a popover detached from its trigger.
//
// Keyed by the DOM node itself: a subscriber registers an `anchorRef` to an element inside its own
// scroll subtree (its trigger). A single capture-phase scroll listener dismisses a subscriber only when
// the scrolled element CONTAINS that anchor — i.e. one of the anchor's ancestors scrolled. This
// self-isolates regions by DOM containment (no scope/ids needed) and never closes a popover when its
// own portaled option list scrolls (the portaled list is not an ancestor of the anchor).

type ScrollDismissSubscriber = {
    anchorRef: RefObject<Element | null>;
    onClose: VoidFunction;
};

const subscribers = new Set<ScrollDismissSubscriber>();

let isListening = false;

// A real user scroll gesture (wheel/touch) emits its input event immediately before the resulting
// `scroll`. A reflow/clamp scroll does not — e.g. picking a multi-select item shrinks the filter card,
// the browser clamps `scrollTop` to the new (smaller) max and fires a synthetic `scroll` with no user
// intent behind it. Gating dismissal on a recent user gesture ignores those false scrolls, so an open
// popover survives the layout shift its own selection caused while still closing on genuine scrolling.
const USER_SCROLL_INTENT_MS = 150;

let lastUserScrollIntentTs = 0;

const markUserScrollIntent = (): void => {
    lastUserScrollIntentTs = performance.now();
};

const handleScroll = (event: Event): void => {
    const scrolled = event.target;

    if (!(scrolled instanceof Node)) {
        return;
    }

    if (performance.now() - lastUserScrollIntentTs > USER_SCROLL_INTENT_MS) {
        return;
    }

    subscribers.forEach((subscriber) => {
        const anchor = subscriber.anchorRef.current;

        if (anchor && scrolled.contains(anchor)) {
            subscriber.onClose();
        }
    });
};

export const subscribeScrollDismiss = (subscriber: ScrollDismissSubscriber): VoidFunction => {
    subscribers.add(subscriber);

    if (!isListening) {
        // Capture phase: scroll events don't bubble, but a capture listener on document still receives
        // them from any scrolling descendant. The wheel/touch listeners stamp user intent just before
        // the resulting scroll, so handleScroll can tell a real scroll from a reflow/clamp one.
        document.addEventListener("scroll", handleScroll, true);
        document.addEventListener("wheel", markUserScrollIntent, { capture: true, passive: true });
        document.addEventListener("touchmove", markUserScrollIntent, { capture: true, passive: true });
        isListening = true;
    }

    return () => {
        subscribers.delete(subscriber);

        if (subscribers.size === 0 && isListening) {
            document.removeEventListener("scroll", handleScroll, true);
            document.removeEventListener("wheel", markUserScrollIntent, true);
            document.removeEventListener("touchmove", markUserScrollIntent, true);
            isListening = false;
        }
    };
};
